/**
 * Formatação de campos que são uma DATA, não um instante: nascimento, admissão,
 * demissão, emissão de documento, vigência.
 *
 * Esses campos são `DateTime` no Prisma por falta de tipo melhor, mas guardam
 * meia-noite (`1925-10-25 00:00:00`) porque a hora não significa nada. O backend
 * serializa a coluna `timestamp without time zone` como UTC
 * (`1925-10-25T00:00:00.000Z`), e aí mora a armadilha: `new Date(...)` no
 * navegador resolve isso para o fuso local, e quem está em America/Sao_Paulo
 * (UTC-3) recua três horas e cai no **dia anterior** — 25/10/1925 vira
 * 24/10/1925 na tela.
 *
 * A correção é ler os componentes da data sem passar pelo fuso nenhum. Use estas
 * funções em todo campo date-only.
 *
 * ATENÇÃO: não use em `createdAt`, `updatedAt` ou qualquer carimbo de momento —
 * ali a conversão para o fuso local é justamente o comportamento certo.
 */

/** Extrai `yyyy-MM-dd` do valor, sem deixar o fuso local deslocar o dia. */
export function toDateOnlyString(
    value?: string | Date | null
): string | null {
    if (!value) return null

    if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) return null
        // Componentes em UTC: é assim que o valor foi serializado pela API.
        const year = value.getUTCFullYear()
        const month = String(value.getUTCMonth() + 1).padStart(2, '0')
        const day = String(value.getUTCDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
    }

    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
    return match ? `${match[1]}-${match[2]}-${match[3]}` : null
}

/**
 * Formata um campo date-only como `dd/MM/yyyy`.
 *
 * @param fallback texto devolvido quando não há data válida (padrão: `-`).
 */
export function formatDateOnly(
    value?: string | Date | null,
    fallback = '-'
): string {
    const isoDate = toDateOnlyString(value)
    if (!isoDate) return fallback

    const [year, month, day] = isoDate.split('-')
    return `${day}/${month}/${year}`
}

/**
 * Idade em anos completos a partir de uma data de nascimento date-only.
 * Comparação feita em UTC, pelo mesmo motivo das funções acima.
 */
export function calculateAgeFromDateOnly(
    value?: string | Date | null
): number | null {
    const isoDate = toDateOnlyString(value)
    if (!isoDate) return null

    const [year, month, day] = isoDate.split('-').map(Number)
    const today = new Date()
    let age = today.getUTCFullYear() - year

    const monthDiff = today.getUTCMonth() + 1 - month
    if (monthDiff < 0 || (monthDiff === 0 && today.getUTCDate() < day)) {
        age -= 1
    }

    return age >= 0 ? age : null
}
